import { Pause, Play, Volume2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MessageAttachmentDto } from "@multi-chat/shared";
import { cn } from "../../../lib/utils.js";
import { AttachmentCardShell } from "../AttachmentCardShell/AttachmentCardShell.js";
import { formatMediaClock, mediaProgressRatio } from "../media-clock.js";
import { pauseHtmlAudio, playHtmlAudio, AUDIO_VISUALLY_HIDDEN_CLASS } from "../play-html-audio.js";
import { useAuthorizedAudioSrc } from "../use-authorized-audio-src.js";
import { useInboxAttachmentUrl } from "../use-inbox-attachment-url.js";
import {
  INITIAL_VOICE_PLAYER,
  clampSeekTime,
  seekRatioFromPointer,
  voicePlayerOnEnded,
  voicePlayerOnError,
  voicePlayerOnLoadedMetadata,
  voicePlayerOnPlayRejected,
  voicePlayerOnTimeUpdate,
  voicePlayerTogglePlay,
} from "../voice-player-state.js";

export function MessageAudioFile({
  organizationId,
  attachment,
}: {
  organizationId: number;
  attachment: MessageAttachmentDto;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { urlResolving, failed } = useInboxAttachmentUrl(organizationId, attachment);
  const { playbackSrc, sourceFailed } = useAuthorizedAudioSrc(
    organizationId,
    attachment.attachmentId,
    attachment.storageState === "STORED" && !failed,
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLButtonElement | null>(null);
  const [player, setPlayer] = useState(INITIAL_VOICE_PLAYER);
  const isRtl = i18n.dir() === "rtl";

  const applyPlayback = useCallback((nextStatus: typeof player.status) => {
    const node = audioRef.current;
    if (!node) return;
    if (nextStatus === "playing") {
      void playHtmlAudio(node).then((result) => {
        if (result.status === "played") {
          return;
        }
        const errorName = result.status === "failed" ? result.errorName : "AbortError";
        setPlayer((current) => voicePlayerOnPlayRejected(current, errorName));
      });
      return;
    }
    pauseHtmlAudio(node);
  }, []);

  const handleToggle = useCallback(() => {
    setPlayer((current) => {
      const next = voicePlayerTogglePlay(current);
      queueMicrotask(() => applyPlayback(next.status));
      return next;
    });
  }, [applyPlayback]);

  if (failed || sourceFailed) {
    return (
      <p className="m-0 text-[0.8125rem] text-ink-subtle">{t("inbox.media.unavailable")}</p>
    );
  }

  const progress = mediaProgressRatio(player.currentTime, player.duration ?? 0);
  const isPlaying = player.status === "playing";
  const srcReady = Boolean(playbackSrc);
  const canControl = srcReady && player.status !== "error" && !urlResolving;
  const title = attachment.fileName?.trim() || t("inbox.replyTo.audio");

  return (
    <AttachmentCardShell>
      {srcReady ? (
        <audio
          ref={audioRef}
          src={playbackSrc}
          preload="metadata"
          className={AUDIO_VISUALLY_HIDDEN_CLASS}
          onLoadedMetadata={(event) => {
            const duration = event.currentTarget.duration;
            setPlayer((current) => voicePlayerOnLoadedMetadata(current, duration));
          }}
          onDurationChange={(event) => {
            const duration = event.currentTarget.duration;
            setPlayer((current) => voicePlayerOnLoadedMetadata(current, duration));
          }}
          onTimeUpdate={(event) => {
            const currentTime = event.currentTarget.currentTime;
            setPlayer((current) => voicePlayerOnTimeUpdate(current, currentTime));
          }}
          onEnded={() => setPlayer(voicePlayerOnEnded)}
          onError={() => setPlayer(voicePlayerOnError)}
        />
      ) : null}

      <button
        type="button"
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-muted text-ink",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
          !canControl && "opacity-60",
        )}
        disabled={!canControl}
        aria-label={isPlaying ? t("inbox.voice.pause") : t("inbox.voice.play")}
        onClick={handleToggle}
      >
        {isPlaying ? (
          <Pause className="size-4 fill-current" aria-hidden="true" />
        ) : (
          <Play className="size-4 translate-x-px fill-current" aria-hidden="true" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <p className="m-0 flex items-center gap-1 truncate text-[0.8125rem] font-medium text-ink">
          <Volume2 className="size-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
          <span className="truncate">{title}</span>
        </p>
        <button
          ref={barRef}
          type="button"
          className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-ink/15 p-0"
          aria-label={t("inbox.voice.seek")}
          disabled={!canControl || player.duration == null}
          onClick={(event) => {
            const rect = barRef.current?.getBoundingClientRect();
            const node = audioRef.current;
            if (!rect || !node) return;
            const ratio = seekRatioFromPointer(event.clientX, rect, isRtl);
            const time = clampSeekTime(ratio * (player.duration ?? 0), player.duration);
            node.currentTime = time;
            setPlayer((current) => ({ ...current, currentTime: time }));
          }}
        >
          <span
            className="block h-full rounded-full bg-accent"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </button>
        <p className="m-0 mt-0.5 text-[10px] tabular-nums text-ink-subtle">
          {formatMediaClock(player.currentTime)}
          {player.duration != null ? ` / ${formatMediaClock(player.duration)}` : ""}
        </p>
      </div>
    </AttachmentCardShell>
  );
}
